// src/types/user.types.ts

import type { Role, ApiRole } from './role.types';
import { mapApiRoleToRole } from './role.types';

/**
 * User interface
 * Modelo extendido de usuario para el frontend
 */
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  phone?: string;
  primaryLocationId?: number | null;
  canSellFromOtherLocations?: boolean;

  // Estado
  isActive: boolean;

  // Bloqueo (independiente de isActive)
  lockedAt?: string | null;
  lockReason?: string | null;

  // Roles asignados
  roles: Role[];

  // Metadatos
  createdAt: string;
  updatedAt: string;
  lastModifiedBy?: string;

  // Campos de bloqueo legacy (opcionales)
  blockedAt?: string;
  blockedBy?: string;
  blockReason?: string;
}

/**
 * UserFilter interface
 * Filtros para búsqueda y filtrado de usuarios
 */
export interface UserFilter {
  searchTerm: string;
  /** Aligns with GET /users `status` query: active | inactive */
  status: 'all' | 'active' | 'inactive';
  roleIds?: string[];
  startDate?: string;
  endDate?: string;
  sortBy?: 'email' | 'firstName' | 'lastName' | 'createdAt' | 'updatedAt';
}

/**
 * UserPaginationState interface
 * Estado de paginación para la tabla de usuarios
 */
export interface UserPaginationState {
  itemsPerPage: number;
  currentPage: number;
  totalItems: number;
}

/**
 * ApiUser interface
 * Modelo de usuario tal como viene del API
 * Basado en el modelo User del backend (src/api/models/User.ts)
 * Nota: El backend NO incluye phone, lastModifiedBy, blockedAt, blockedBy, blockReason
 * Estos campos pueden venir en respuestas específicas o ser agregados en el frontend
 */
export interface ApiUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  roles: ApiRole[];
  primaryLocationId?: number | null;
  canSellFromOtherLocations?: boolean;
  lockedAt?: string | null;
  lockReason?: string | null;
  phone?: string;
  lastModifiedBy?: string;
  blockedAt?: string;
  blockedBy?: string;
  blockReason?: string;
}

/**
 * ApiResponse interface for Users
 * Respuesta paginada del API
 */
export interface UserApiResponse {
  data: ApiUser[];
  meta?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * CreateUserDto interface
 * DTO para crear un nuevo usuario
 * Basado en el modelo CreateUserDto del backend (src/api/models/CreateUserDto.ts)
 */
export interface CreateUserDto {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  roleIds?: string[]; // Opcional en el backend
  isActive?: boolean;
  primaryLocationId?: number;
  canSellFromOtherLocations?: boolean;
}

/**
 * UpdateUserDto interface
 * DTO para actualizar un usuario existente
 * Basado en el modelo UpdateUserDto del backend (src/api/models/UpdateUserDto.ts)
 */
export interface UpdateUserDto {
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  roleIds?: string[];
  isActive?: boolean;
  primaryLocationId?: number | null;
  canSellFromOtherLocations?: boolean;
}

/**
 * UserWizardData interface
 * Datos del wizard de usuarios
 */
export interface UserWizardData {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  roleIds: string[];
  isActive: boolean;
  primaryLocationId?: number | null;
  canSellFromOtherLocations?: boolean;
}

/**
 * Helper para mapear ApiUser a User
 * Maneja campos que pueden no venir del backend estándar
 */
export function mapApiUserToUser(apiUser: ApiUser | any): User {
  // El backend siempre devuelve roles anidados (Array<Role>)
  const roles = (apiUser.roles || []).map((role: any) => mapApiRoleToRole(role));
  const fullName = `${apiUser.firstName} ${apiUser.lastName}`.trim();

  return {
    id: apiUser.id,
    email: apiUser.email,
    firstName: apiUser.firstName,
    lastName: apiUser.lastName,
    fullName,
    phone: apiUser.phone,
    primaryLocationId: apiUser.primaryLocationId ?? null,
    canSellFromOtherLocations: apiUser.canSellFromOtherLocations ?? false,
    isActive: apiUser.isActive ?? true,
    lockedAt: apiUser.lockedAt ?? null,
    lockReason: apiUser.lockReason ?? null,
    roles,
    createdAt: apiUser.createdAt,
    updatedAt: apiUser.updatedAt,
    lastModifiedBy: apiUser.lastModifiedBy,
    blockedAt: apiUser.blockedAt,
    blockedBy: apiUser.blockedBy,
    blockReason: apiUser.blockReason,
  };
}
